// Calendar arithmetic on `YYYY-MM-DD` strings, and nothing else.
//
// Every local date in this product is a plain ISO day: the instant lives in
// `starts_at_utc`, the wall clock lives in `lib/timezone.js`, and everything in
// between — buckets, windows, ranges, pagination — moves whole days around.
// That single operation had eight copies (the materialiser, the feed router,
// the archive, the public API, the map, the digest, the series shift and the
// recurrence field in the backend), all correct and none shared, which is how
// `daysBetween` came to mean two different things in three files.
//
// Deliberately not a date library. Anchoring at midnight UTC is what makes this
// arithmetic and not timekeeping: a local date has no offset to preserve, so
// adding 86 400 000 ms can never land on the wrong day the way it would on a
// zoned instant. Anything that needs a zone goes through `lib/timezone.js`.

/** The day `days` away from `date`, forwards or backwards. */
export function shiftDate(date, days) {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * 86400000).toISOString().slice(0, 10);
}

/**
 * Days from `from` to `to`, counting the gaps. Two consecutive days are 1
 * apart, and the same day is 0.
 */
export function daysBetween(from, to) {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
}

/**
 * Days from `from` to `to`, counting the days themselves — a single-day range
 * spans 1, not 0.
 *
 * It has its own name because it had none: the feed router's `daysBetween` was
 * this one with a `+ 1`, while the two in `/admin` and `/panel` were the other,
 * and reading a call site told you nothing about which answer you were getting.
 * A selection's *span* decides whether the feed heads by weekday or by month
 * (see `bucketFor`), so the off-by-one is a visible one.
 */
export function daysInclusive(from, to) {
  return daysBetween(from, to) + 1;
}
